<template>
  <button
    class="flex h-20 w-full cursor-pointer flex-row items-center space-x-3 p-3"
    type="button"
    @contextmenu.prevent
  >
    <div
      class="relative flex size-12 shrink-0 items-center justify-center rounded bg-primary text-white"
      :style="{ backgroundColor: product.color }"
    >
      {{ productShortString }}
      <template v-if="productInCartQuantity">
        <div
          class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-primary bg-background px-1 text-xs text-primary"
        >
          <span class="line-clamp-1 font-semibold">
            {{ productInCartQuantity }}
          </span>
        </div>
      </template>
    </div>
    <div class="flex-1 overflow-hidden">
      <div class="line-clamp-2 text-left text-sm">
        {{ product.name }}
      </div>
      <div
        class="flex w-full flex-1 space-x-1 overflow-x-auto"
      >
        <span
          v-for="tag in tags"
          :key="tag.id"
          class="rounded-full bg-background-fg/30 px-2 py-px text-xs text-background"
          :style="{
            backgroundColor:
              PRODUCT_TAG_COLOR_MAP[tag.color].color,
          }"
        >
          {{ tag.label }}
        </span>
      </div>
    </div>

    <div
      class="flex w-24 shrink-0 items-center justify-end"
    >
      <p
        class="line-clamp-1 text-right text-sm font-semibold"
      >
        {{ priceString }}
      </p>
    </div>
  </button>
</template>

<script setup lang="ts">
import type { Product } from "@/services/sharedTypes/products"
import {
  type ProductTag,
  useProductTags,
} from "@/services/product-tags/listProductTags"
import { PRODUCT_TAG_COLOR_MAP } from "@/services/product-tags/productTagColors"

const { getProductCount } = useCurrentOrder()
const {
  merchantCurrencySymbol,
  formatToMerchantCurrencyString,
} = useCurrency()

const props = defineProps<{
  product: Product
}>()

const priceString = computed(() =>
  props.product.hasVariablePrice
    ? `${merchantCurrencySymbol.value} variable`
    : formatToMerchantCurrencyString(props.product.price)
)

const productShortString = computed(() => {
  const strippedName = props.product.name
    .toLowerCase()
    .replace(" ", "")
  return `${strippedName
    .charAt(0)
    .toUpperCase()}${strippedName.charAt(1)}`
})

const productInCartQuantity = computed(() => {
  return getProductCount(props.product.id)
})

const { data: productTags } = useProductTags()
const tags = computed(() =>
  productTags.value
    ? props.product.tagIds.slice(0, 10).reduce((agg, v) => {
        const tag = productTags.value.find(
          (_tag) => _tag.id === v && !_tag.isHidden
        )

        return tag ? [...agg, tag] : agg
      }, [] as ProductTag[])
    : []
)
</script>
